import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Search, SlidersHorizontal, FileText, MousePointerClick, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { useUser } from '@/providers/user-provider';
import { getDismissedTutorialsService, dismissTutorialService } from '@/services/auth-service';

const TUTORIAL_ID = 'search';

type Placement = 'bottom' | 'right' | 'top' | 'left';

interface TourStep {
    target: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    color: string;
    placement: Placement;
}

const STEPS: TourStep[] = [
    {
        target: 'search-bar',
        icon: <Search className="size-5" />,
        title: 'Search for Podcasts',
        description: 'Type a podcast name, author, or topic here and hit Enter to find shows.',
        color: 'bg-blue-100 text-blue-600',
        placement: 'bottom',
    },
    {
        target: 'filters-sidebar',
        icon: <SlidersHorizontal className="size-5" />,
        title: 'Refine with Filters',
        description: 'Narrow your results by rating, listeners, episode count, language, region, and more.',
        color: 'bg-purple-100 text-purple-600',
        placement: 'right',
    },
    {
        target: 'episode-keywords',
        icon: <FileText className="size-5" />,
        title: 'Find Topic-Relevant Shows',
        description: 'Add keywords to discover podcasts with episodes about specific topics — great for niche outreach.',
        color: 'bg-amber-100 text-amber-600',
        placement: 'right',
    },
    {
        target: 'results-area',
        icon: <MousePointerClick className="size-5" />,
        title: 'Browse & Take Action',
        description: 'Click any podcast to view details and episodes. Use the + icon to add it to an outreach campaign.',
        color: 'bg-emerald-100 text-emerald-600',
        placement: 'bottom',
    },
];

interface TooltipPos {
    top: number;
    left: number;
    arrowStyle: React.CSSProperties;
}

function computePosition(rect: DOMRect, placement: Placement, ttW: number, ttH: number): TooltipPos {
    const GAP = 14;
    const ARR = 8;
    let top = 0, left = 0;
    const arrowStyle: React.CSSProperties = {
        position: 'absolute',
        width: 0,
        height: 0,
        borderLeft: `${ARR}px solid transparent`,
        borderRight: `${ARR}px solid transparent`,
        borderBottom: `${ARR}px solid white`,
    };

    switch (placement) {
        case 'bottom':
            top = rect.bottom + GAP;
            left = rect.left + rect.width / 2 - ttW / 2;
            arrowStyle.top = -ARR;
            arrowStyle.left = ttW / 2 - ARR;
            arrowStyle.transform = 'rotate(0deg)';
            break;
        case 'top':
            top = rect.top - ttH - GAP;
            left = rect.left + rect.width / 2 - ttW / 2;
            arrowStyle.top = ttH;
            arrowStyle.left = ttW / 2 - ARR;
            arrowStyle.transform = 'rotate(180deg)';
            break;
        case 'right':
            top = rect.top + rect.height / 2 - ttH / 2;
            left = rect.right + GAP;
            arrowStyle.top = ttH / 2 - ARR;
            arrowStyle.left = -ARR;
            arrowStyle.transform = 'rotate(270deg)';
            break;
        case 'left':
            top = rect.top + rect.height / 2 - ttH / 2;
            left = rect.left - ttW - GAP;
            arrowStyle.top = ttH / 2 - ARR;
            arrowStyle.left = ttW;
            arrowStyle.transform = 'rotate(90deg)';
            break;
    }

    // Keep within viewport
    const pad = 12;
    left = Math.max(pad, Math.min(left, window.innerWidth - ttW - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - ttH - pad));

    return { top, left, arrowStyle };
}

export function SearchTutorial() {
    const { user } = useUser();
    const [showPrompt, setShowPrompt] = useState(false);
    const [active, setActive] = useState(false);
    const [step, setStep] = useState(0);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<TooltipPos | null>(null);

    useEffect(() => {
        if (!user) return;
        const cancelled = false;
        getDismissedTutorialsService()
            .then((dismissed) => {
                if (!cancelled && dismissed !== null && !(TUTORIAL_ID in dismissed)) {
                    setTimeout(() => setShowPrompt(true), 800);
                }
            });
    }, [user]);

    const measure = useCallback(() => {
        if (!active) return;
        const el = document.querySelector(`[data-tour="${STEPS[step].target}"]`);
        if (el) {
            const rect = el.getBoundingClientRect();
            setTargetRect(rect);

            // Scroll into view if off-screen
            if (rect.top < 0 || rect.bottom > window.innerHeight) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => setTargetRect(el.getBoundingClientRect()), 350);
            }
        }
    }, [active, step]);

    useEffect(() => {
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [measure]);

    useLayoutEffect(() => {
        if (!targetRect || !tooltipRef.current) return;
        const tt = tooltipRef.current.getBoundingClientRect();
        setPos(computePosition(targetRect, STEPS[step].placement, tt.width, tt.height));
    }, [targetRect, step]);

    const dismiss = useCallback((status: 'COMPLETED' | 'SKIPPED' | 'LATER') => {
        setActive(false);
        setShowPrompt(false);
        if (status !== 'LATER') {
            dismissTutorialService(TUTORIAL_ID, status === 'COMPLETED' ? 'COMPLETED' : 'SKIPPED');
        }
    }, []);

    const skip = useCallback(() => dismiss('SKIPPED'), [dismiss]);
    const finish = useCallback(() => dismiss('COMPLETED'), [dismiss]);
    const later = useCallback(() => dismiss('LATER'), [dismiss]);
    const startTutorial = useCallback(() => {
        setShowPrompt(false);
        setActive(true);
    }, []);

    const next = () => {
        if (step < STEPS.length - 1) { setStep(step + 1); setPos(null); }
        else finish();
    };

    const prev = () => {
        if (step > 0) { setStep(step - 1); setPos(null); }
    };

    if (!user) return null;

    // Render initial prompt dialog
    if (showPrompt && !active) {
        return createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
                {/* Dark overlay */}
                <div
                    className="absolute inset-0 bg-black/50"
                    onClick={later}
                />

                {/* Prompt dialog */}
                <div className="relative bg-white rounded-xl shadow-2xl border border-border/50 w-96 p-6 max-w-[calc(100%-2rem)]">
                    <button onClick={later} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors">
                        <X className="size-4" />
                    </button>

                    <div className="mb-4">
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Learn How to Search</h2>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            We'll show you how to find the perfect podcasts for your outreach campaigns. This quick tour will teach you about searching, filtering, and using episode keywords.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                        <Button variant="ghost" onClick={skip} className="flex-1 text-sm h-9">
                            Skip
                        </Button>
                        <Button variant="outline" onClick={later} className="flex-1 text-sm h-9">
                            Later
                        </Button>
                        <Button onClick={startTutorial} className="flex-1 text-sm h-9 shadow-sm shadow-primary/20">
                            Start
                        </Button>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    if (!active || !targetRect) return null;

    const cur = STEPS[step];
    const pad = 8;

    return createPortal(
        <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: 'none' }}>
            {/* Dark overlay with spotlight cutout */}
            <div
                className="absolute inset-0 bg-black/50 transition-all duration-300"
                style={{
                    pointerEvents: 'auto',
                    clipPath: `polygon(
                        0% 0%, 0% 100%,
                        ${targetRect.left - pad}px 100%,
                        ${targetRect.left - pad}px ${targetRect.top - pad}px,
                        ${targetRect.right + pad}px ${targetRect.top - pad}px,
                        ${targetRect.right + pad}px ${targetRect.bottom + pad}px,
                        ${targetRect.left - pad}px ${targetRect.bottom + pad}px,
                        ${targetRect.left - pad}px 100%,
                        100% 100%, 100% 0%
                    )`,
                }}
                onClick={skip}
            />

            {/* Highlight ring around target */}
            <div
                className="absolute rounded-lg ring-2 ring-primary/60 transition-all duration-300"
                style={{
                    pointerEvents: 'none',
                    top: targetRect.top - pad,
                    left: targetRect.left - pad,
                    width: targetRect.width + pad * 2,
                    height: targetRect.height + pad * 2,
                }}
            />

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="absolute bg-white rounded-xl shadow-2xl border border-border/50 w-80 transition-all duration-300"
                style={{
                    pointerEvents: 'auto',
                    top: pos?.top ?? -9999,
                    left: pos?.left ?? -9999,
                    opacity: pos ? 1 : 0,
                }}
            >
                {pos && <div style={pos.arrowStyle} />}

                <button onClick={skip} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors">
                    <X className="size-4" />
                </button>

                <div className="p-5">
                    {/* Progress */}
                    <div className="flex gap-1.5 mb-4">
                        {STEPS.map((_, i) => (
                            <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-primary' : 'bg-slate-200'}`} />
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex gap-3 items-start">
                        <div className={`shrink-0 size-10 rounded-xl flex items-center justify-center ${cur.color}`}>
                            {cur.icon}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 mb-1">{cur.title}</h3>
                            <p className="text-xs text-gray-500 leading-relaxed">{cur.description}</p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={prev} disabled={step === 0} className="text-xs gap-1 h-7 px-2">
                                <ArrowLeft className="size-3" /> Back
                            </Button>
                            <span className="text-[10px] text-slate-400">{step + 1}/{STEPS.length}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={skip} className="text-xs h-7 px-2 text-slate-500">Skip</Button>
                            <Button size="sm" onClick={next} className="text-xs gap-1 h-7 px-3 shadow-sm shadow-primary/20">
                                {step < STEPS.length - 1 ? (<>Next <ArrowRight className="size-3" /></>) : "Got it!"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
